import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { getClientVendor, updateClientVendor } from "../../api/clientVendorsApi";
import { getClientPortalView } from "../../api/clientPortalApi";
import VendorFormModal, { EMPTY_VENDOR_FORM } from "../../components/clientPortal/VendorFormModal";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { stripVendorImportNotes } from "../../clientViewRuntime/utils/golfHomesVendorDisplay";
import {
  formatClientDate,
  formatClientDateTime,
  formatStatusLabel,
} from "../../utils/clientPortalFormat";
import {
  formatWebsiteLabel,
  vendorCompanyLine,
  vendorDisplayName,
  vendorInitials,
  vendorTypeColor,
  vendorTypeIcon,
} from "../../utils/clientVendorUi";
import { resolveClientPortalAssetUrl } from "../../api/clientUploadApi";

const ClientVendorDetailPage = () => {
  const { clientViewId, vendorId } = useParams();
  const [viewName, setViewName] = useState("");
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_VENDOR_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const paths = useClientPortalPaths();

  const listPath = paths.viewSection(clientViewId, "realtors-vendors");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [viewBody, vendorBody] = await Promise.all([
        getClientPortalView(clientViewId),
        getClientVendor(clientViewId, vendorId),
      ]);
      setViewName(viewBody.clientView?.name || "Client View");
      setVendor(vendorBody.vendor);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load this professional.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => {
    if (!vendor) return;
    setEditForm({
      vendorName: vendor.vendorName || "",
      vendorType: vendor.vendorType || "Other",
      website: vendor.website || "",
      photoUrl: vendor.photoUrl || "",
      phone: vendor.phone || "",
      email: vendor.email || "",
      notes: vendor.notes || "",
    });
    setEditError("");
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError("");
    try {
      const body = await updateClientVendor(clientViewId, vendorId, editForm);
      setVendor(body.vendor);
      setEditOpen(false);
      setSuccessMessage("Professional updated successfully.");
    } catch (error) {
      setEditError(error.message || "Unable to update professional.");
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return <ClientPortalLoading message="Loading professional…" />;
  }

  if (errorMessage) {
    return (
      <>
        <ClientPortalError message={errorMessage} />
        <p className="cp-vendors-back-wrap">
          <Link to={listPath} className="cp-vendors-back-link">
            ← Back to Realtors &amp; Vendors
          </Link>
        </p>
      </>
    );
  }

  if (!vendor) {
    return <ClientPortalError message="Professional not found." />;
  }

  const displayName = vendorDisplayName(vendor);
  const company = vendorCompanyLine(vendor);
  const publicNotes = stripVendorImportNotes(vendor.notes);
  const typeColor = vendorTypeColor(vendor.vendorType);
  const photoStyle = vendor.photoUrl
    ? { backgroundImage: `url('${resolveClientPortalAssetUrl(vendor.photoUrl).replace(/'/g, "%27")}')` }
    : undefined;

  return (
    <div className="cp-vendor-detail-page">
      {successMessage ? (
        <section className="panel-card client-vendor-toast cp-vendors-toast">
          <p className="muted">
            {successMessage}{" "}
            <button type="button" className="cv-button-link" onClick={() => setSuccessMessage("")}>
              Dismiss
            </button>
          </p>
        </section>
      ) : null}

      <p className="cp-vendors-breadcrumb eyebrow">
        <Link to={paths.view(clientViewId)}>{viewName}</Link>
        {" / "}
        <Link to={listPath}>Realtors &amp; Vendors</Link>
        {" / "}
        <span>{displayName}</span>
      </p>

      <section className="cp-vendor-detail-hero panel-card">
        <div
          className={`cp-vendor-detail-photo${vendor.photoUrl ? "" : " cp-vendor-detail-photo--placeholder"}`}
          style={photoStyle}
        >
          {!vendor.photoUrl ? (
            <span className="cp-vendor-detail-initials" aria-hidden="true">
              {vendorInitials(displayName)}
            </span>
          ) : null}
        </div>
        <div className="cp-vendor-detail-hero-body">
          <div className="cp-vendor-detail-badges">
            <span
              className="cp-vendor-type-pill"
              style={{ "--vendor-type-color": typeColor }}
            >
              <span aria-hidden="true">{vendorTypeIcon(vendor.vendorType)}</span>
              {vendor.vendorType || "Other"}
            </span>
            <span className={`status-badge status-badge--${vendor.status || "active"}`}>
              {formatStatusLabel(vendor.status)}
            </span>
          </div>
          <h1 className="cp-vendor-detail-name">{displayName}</h1>
          {company ? <p className="cp-vendor-detail-company">{company}</p> : null}
          <div className="cp-vendor-detail-hero-actions">
            <button type="button" className="btn btn-secondary" onClick={openEdit}>
              Edit profile
            </button>
            {vendor.email ? (
              <a href={`mailto:${vendor.email}`} className="btn btn-primary">
                Email
              </a>
            ) : null}
            {vendor.phone ? (
              <a href={`tel:${vendor.phone}`} className="btn btn-secondary">
                Call
              </a>
            ) : null}
          </div>
        </div>
      </section>

      <div className="cp-vendor-detail-grid">
        <section className="panel-card cp-vendor-detail-panel">
          <h2>Contact</h2>
          <dl className="cp-vendor-detail-dl">
            <div>
              <dt>Email</dt>
              <dd>
                {vendor.email ? (
                  <a href={`mailto:${vendor.email}`}>{vendor.email}</a>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>
                {vendor.phone ? <a href={`tel:${vendor.phone}`}>{vendor.phone}</a> : "—"}
              </dd>
            </div>
            <div>
              <dt>Website</dt>
              <dd>
                {vendor.website ? (
                  <a href={vendor.website} target="_blank" rel="noreferrer">
                    {formatWebsiteLabel(vendor.website)}
                  </a>
                ) : (
                  "—"
                )}
              </dd>
            </div>
          </dl>
        </section>

        <section className="panel-card cp-vendor-detail-panel">
          <h2>About</h2>
          {publicNotes ? (
            <p className="cp-vendor-detail-notes">{publicNotes}</p>
          ) : (
            <p className="muted">No notes on file for this professional.</p>
          )}
        </section>

        <section className="panel-card cp-vendor-detail-panel">
          <h2>Record</h2>
          <dl className="cp-vendor-detail-dl">
            <div>
              <dt>Business name</dt>
              <dd>{vendor.vendorName || "—"}</dd>
            </div>
            <div>
              <dt>Added</dt>
              <dd>{formatClientDate(vendor.createdAt)}</dd>
            </div>
            <div>
              <dt>Last updated</dt>
              <dd>{formatClientDateTime(vendor.updatedAt)}</dd>
            </div>
          </dl>
        </section>
      </div>

      <p className="cp-vendors-back-wrap">
        <Link to={listPath} className="cp-vendors-back-link">
          ← Back to all professionals
        </Link>
      </p>

      {editOpen ? (
        <VendorFormModal
          title="Edit Professional"
          form={editForm}
          setForm={setEditForm}
          onSubmit={handleEditSubmit}
          onClose={() => setEditOpen(false)}
          saving={editSaving}
          error={editError}
          submitLabel="Save Changes"
          clientViewId={clientViewId}
          vendorId={vendorId}
        />
      ) : null}
    </div>
  );
};

export default ClientVendorDetailPage;
